import { Instagram, Facebook, Youtube } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const Footer = ({ hideSpravato = false }: { hideSpravato?: boolean } = {}) => {
  return (
    <footer
      className="relative overflow-hidden bg-primary text-primary-foreground pt-5 pb-5"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }}
    >
      <div className="pointer-events-none absolute -left-20 -top-20 h-48 w-48 rounded-full bg-accent/5 blur-3xl" />
      <div className="container-px relative mx-auto max-w-7xl">
        {/* Top Header Block */}
        <div className="flex flex-col gap-3">
          <Link to="/" className="inline-block">
            <img
              src="https://vibe.filesafe.space/1787327131284421342/attachments/1e90129f-e6ef-4b0e-b3f2-a8376c67f0d3.png"
              alt="Neuro Wellness TMS Centers of America"
              className="h-10 w-auto brightness-0 invert"
              width={326}
              height={80}
              loading="eager"
            />
          </Link>
          <p className="max-w-6xl text-base leading-relaxed text-primary-foreground/80 font-normal">
            Neuro Wellness TMS Centers of America is an outpatient psychiatric
            neuromodulation center in Coral Springs, Florida providing advanced
            treatment options for depression, including SAINT® TMS, conventional
            TMS{hideSpravato ? "" : ", SPRAVATO®"}, and accelerated
            neuromodulation options. We serve patients in Broward, Miami-Dade,
            and Palm Beach counties and are an in-network provider with the
            Veterans Administration and Medicare. Contact us to start your
            consultation.
          </p>
        </div>

        <hr className="my-4 border-primary-foreground/15" />

        <div className="mb-4">
          <LanguageSwitcher variant="dark" />
        </div>

        {/* Quick Links — Treatment & Practice navigation for sitewide backlinking */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:gap-6 mb-4">
          <div className="space-y-1.5">
            <h4 className="font-headings text-sm font-bold uppercase tracking-wider text-white">
              Treatments
            </h4>
            <ul className="space-y-1 text-sm text-primary-foreground/80">
              <li>
                <Link
                  to="/saint"
                  className="hover:text-accent transition-colors"
                >
                  SAINT® TMS
                </Link>
              </li>
              <li>
                <Link
                  to="/ampa"
                  className="hover:text-accent transition-colors"
                >
                  Ampa One Day
                </Link>
              </li>
              <li>
                <Link
                  to="/services/tms"
                  className="hover:text-accent transition-colors"
                >
                  TMS Therapy
                </Link>
              </li>
              <li>
                <Link
                  to="/prism"
                  className="hover:text-accent transition-colors"
                >
                  Prism for PTSD &amp; Depression
                </Link>
              </li>
              <li>
                <Link
                  to="/prolivrx"
                  className="hover:text-accent transition-colors"
                >
                  Proliv®Rx
                </Link>
              </li>
            </ul>
          </div>
          <div className="space-y-1.5">
            <h4 className="font-headings text-sm font-bold uppercase tracking-wider text-white">
              TMS by Condition
            </h4>
            <ul className="space-y-1 text-sm text-primary-foreground/80">
              <li>
                <Link
                  to="/services/depression/"
                  className="hover:text-accent transition-colors"
                >
                  TMS for Depression
                </Link>
              </li>
              <li>
                <Link
                  to="/services/anxiety/"
                  className="hover:text-accent transition-colors"
                >
                  TMS for Anxiety
                </Link>
              </li>
              <li>
                <Link
                  to="/services/ptsd/"
                  className="hover:text-accent transition-colors"
                >
                  TMS for PTSD
                </Link>
              </li>
              <li>
                <Link
                  to="/services/ocd/"
                  className="hover:text-accent transition-colors"
                >
                  TMS for OCD
                </Link>
              </li>
              <li>
                <Link
                  to="/services/psychotherapy"
                  className="hover:text-accent transition-colors"
                >
                  Psychotherapy
                </Link>
              </li>
            </ul>
          </div>
          <div className="space-y-1.5">
            <h4 className="font-headings text-sm font-bold uppercase tracking-wider text-white">
              Practice
            </h4>
            <ul className="space-y-1 text-sm text-primary-foreground/80">
              <li>
                <Link
                  to="/about"
                  className="hover:text-accent transition-colors"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/contactus"
                  className="hover:text-accent transition-colors"
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link
                  to="/cost-insurance"
                  className="hover:text-accent transition-colors"
                >
                  Cost & Insurance
                </Link>
              </li>
              <li>
                <Link
                  to="/patient-eligibility-checklist"
                  className="hover:text-accent transition-colors"
                >
                  Provider Referral
                </Link>
              </li>
              <li>
                <Link
                  to="/contents/patient-info"
                  className="hover:text-accent transition-colors"
                >
                  Patient Info
                </Link>
              </li>
              <li>
                <Link
                  to="/learn/travel-for-saint-tms"
                  className="hover:text-accent transition-colors"
                >
                  Traveling for Treatment
                </Link>
              </li>
              <li>
                <Link
                  to="/careers"
                  className="hover:text-accent transition-colors"
                >
                  Careers
                </Link>
              </li>
            </ul>
          </div>
          <div className="space-y-1.5">
            <h4 className="font-headings text-sm font-bold uppercase tracking-wider text-white">
              Resources
            </h4>
            <ul className="space-y-1 text-sm text-primary-foreground/80">
              <li>
                <Link
                  to="/articles"
                  className="hover:text-accent transition-colors"
                >
                  Articles
                </Link>
              </li>
              <li>
                <Link
                  to="/contents/patient-info/videos-media"
                  className="hover:text-accent transition-colors"
                >
                  Videos & Media
                </Link>
              </li>
              <li>
                <Link
                  to="/tms-coral-springs-fl"
                  className="hover:text-accent transition-colors"
                >
                  TMS Coral Springs
                </Link>
              </li>
              <li>
                <Link
                  to="/am-i-a-candidate"
                  className="hover:text-accent transition-colors"
                >
                  Am I a Candidate?
                </Link>
              </li>
              <li>
                <Link
                  to="/compare-treatments"
                  className="hover:text-accent transition-colors"
                >
                  Compare Treatments
                </Link>
              </li>
              <li>
                <Link
                  to="/clinical-research"
                  className="hover:text-accent transition-colors"
                >
                  Clinical Research
                </Link>
              </li>
              <li>
                <Link
                  to="/depression-treatment-not-working"
                  className="hover:text-accent transition-colors"
                >
                  When Treatment Isn&apos;t Working
                </Link>
              </li>
              <li>
                <Link
                  to="/learn/saint-tms-locations"
                  className="hover:text-accent transition-colors"
                >
                  Verified SAINT® Provider
                </Link>
              </li>
              <li>
                <Link
                  to="/for-families"
                  className="hover:text-accent transition-colors"
                >
                  For Families
                </Link>
              </li>
              <li>
                <Link
                  to="/namiwalks"
                  className="hover:text-accent transition-colors text-white font-medium"
                >
                  NAMIWalks 2026
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <hr className="my-4 border-primary-foreground/15" />
        <div className="grid gap-5 lg:grid-cols-3 lg:gap-8 items-start">
          {/* Column 1: CONTACT US */}
          <div className="space-y-2">
            <h4 className="font-headings text-base font-bold uppercase tracking-wider text-primary-foreground">
              CONTACT US
            </h4>
            <div className="space-y-1 text-base text-primary-foreground/80 leading-relaxed">
              <p>
                9734 West Sample Road, 2nd Floor - Suite B
                <br />
                Coral Springs, FL 33065
              </p>
              <p>
                <a
                  href="tel:9548279793"
                  className="font-semibold hover:text-accent transition-colors"
                >
                  954-827-9793
                </a>
              </p>
            </div>

            {/* Social Icons */}
            <div className="flex items-center gap-2 text-primary-foreground/90">
              <a
                href="https://www.instagram.com/neurowellnesstmscenters/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 rounded-full bg-white/5 hover:bg-white/10 hover:text-accent transition-all"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="https://www.facebook.com/neurowellnesscentersofamerica/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 rounded-full bg-white/5 hover:bg-white/10 hover:text-accent transition-all"
                aria-label="Facebook"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href="https://www.youtube.com/@NeuroWellnessCentersofAmerica"
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 rounded-full bg-white/5 hover:bg-white/10 hover:text-accent transition-all"
                aria-label="YouTube"
              >
                <Youtube className="h-4 w-4" />
              </a>
            </div>
          </div>
          {/* Column 2: OFFICE HOURS */}
          <div className="space-y-2">
            <h4 className="font-headings text-base font-bold uppercase tracking-wider text-primary-foreground">
              OFFICE HOURS
            </h4>
            <div className="space-y-0.5 text-base text-primary-foreground/80">
              <div className="flex justify-between border-b border-white/10 pb-1">
                <span>Monday</span>
                <span className="font-mono">7:00 am - 7:00 pm</span>
              </div>
              <div className="flex justify-between border-b border-white/10 pb-1">
                <span>Tuesday</span>
                <span className="font-mono">7:00 am - 8:00 pm</span>
              </div>
              <div className="flex justify-between border-b border-white/10 pb-1">
                <span>Wednesday</span>
                <span className="font-mono">7:00 am - 7:00 pm</span>
              </div>
              <div className="flex justify-between border-b border-white/10 pb-1">
                <span>Thursday</span>
                <span className="font-mono">7:00 am - 8:00 pm</span>
              </div>
              <div className="flex justify-between border-b border-white/10 pb-1">
                <span>Friday</span>
                <span className="font-mono">7:00 am - 7:00 pm</span>
              </div>
              <div className="flex justify-between border-b border-white/10 pb-1">
                <span>Saturday</span>
                <span className="font-mono">9:00 am - 1:00 pm</span>
              </div>
              <div className="flex justify-between pb-1 text-white font-medium">
                <span>Sunday</span>
                <span>Closed</span>
              </div>
            </div>
            <p className="text-sm italic text-primary-foreground/75 leading-normal">
              * Please note the front desk does not answer phone calls until 9
              am
            </p>
          </div>

          {/* Column 3: ACCREDITATIONS & PARTNERS */}
          <div className="space-y-2">
            <h4 className="font-headings text-base font-bold uppercase tracking-wider text-primary-foreground">
              AFFILIATIONS & ACCREDITATIONS
            </h4>
            <div className="grid grid-cols-2 gap-2 sm:gap-2 items-center">
              {/* ASKP3 */}
              <div className="p-3 bg-white/5 rounded-lg border border-white/10 flex items-center justify-center h-16">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/477c1028-ef96-4a54-9487-6f5524cd310c.png"
                  alt="ASKP3 Member"
                  className="max-h-10 w-auto max-w-full object-contain brightness-0 invert"
                  width={120}
                  height={40}
                  loading="lazy"
                />
              </div>

              {/* Apollo TMS */}
              <div className="p-3 bg-white/5 rounded-lg border border-white/10 flex items-center justify-center h-16">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/b01abdc3-6a82-41cd-8e62-32265e01d608.webp"
                  alt="Apollo TMS Therapy"
                  className="max-h-10 w-auto max-w-full object-contain"
                  width={120}
                  height={40}
                  loading="lazy"
                />
              </div>

              {/* AHCA Florida */}
              <div className="p-3 bg-white/5 rounded-lg border border-white/10 flex items-center justify-center h-16">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/e352e889-09f1-4317-bca6-5fecb40fd3de.webp"
                  alt="AHCA State of Florida"
                  className="max-h-10 w-auto max-w-full object-contain brightness-0 invert"
                  width={120}
                  height={40}
                  loading="lazy"
                />
              </div>

              {/* Clinical TMS Society */}
              <div className="p-3 bg-white/5 rounded-lg border border-white/10 flex items-center justify-center h-16">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/0f59386c-c20a-4f3b-a113-eb569fab07f3.webp"
                  alt="Clinical TMS Society"
                  className="max-h-10 w-auto max-w-full object-contain brightness-0 invert"
                  width={120}
                  height={40}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Google Map embed */}
        <div className="mt-5 overflow-hidden rounded-lg border border-white/10">
          <iframe
            title="Neuro Wellness TMS Centers of America location map"
            src="https://www.google.com/maps?cid=15643352170883590469&g_mp=CiVnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLkdldFBsYWNlEAMYASAF&hl=en&gl=US&source=embed&output=embed"
            width="100%"
            height="220"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>

        {/* Crisis line — subtle */}
        <p className="mt-4 text-center text-xs text-primary-foreground/85">
          If you're in crisis, call or text{" "}
          <a
            href="tel:988"
            className="font-medium text-primary-foreground/85 underline underline-offset-2 hover:text-accent"
          >
            988
          </a>{" "}
          (Suicide & Crisis Lifeline, 24/7) or call{" "}
          <a
            href="tel:911"
            className="font-medium text-primary-foreground/85 underline underline-offset-2 hover:text-accent"
          >
            911
          </a>
          .
        </p>

        {/* Bottom copyright line */}
        <div className="mt-3 flex flex-col items-center justify-between gap-2 border-t border-primary-foreground/15 pt-3 text-center text-base text-primary-foreground/85 sm:flex-row sm:text-left">
          <p>
            © {new Date().getFullYear()} Neuro Wellness TMS Centers of America.
            All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <Link
              to="/accessibility-statement"
              className="hover:text-accent transition-colors"
            >
              Accessibility Statement
            </Link>
            <span>•</span>
            <Link
              to="/privacy-policy"
              className="hover:text-accent transition-colors"
            >
              Privacy Policy
            </Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-accent transition-colors">
              Terms of Service
            </Link>
            <span>•</span>
            <Link
              to="/acceptable-use"
              className="hover:text-accent transition-colors"
            >
              Acceptable Use
            </Link>
            <span>•</span>
            <Link
              to="/learn/saint-disclaimer"
              className="hover:text-accent transition-colors"
            >
              SAINT\u00ae Disclaimer
            </Link>
            <span>•</span>
            <Link to="/sitemap" className="hover:text-accent transition-colors">
              Sitemap
            </Link>
            <span>•</span>
            <Link
              to="/patient-eligibility-checklist"
              className="hover:text-accent transition-colors"
            >
              Physician Referral Checklist
            </Link>
            <span>•</span>
            <span className="text-primary-foreground/70">
              TMS Therapy is FDA-cleared
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
